<template>
  <div class="deposit-overview">
    <div class="title">
      Overview
    </div>

    <div class="warning" v-if="getTotalFee() === null">
      <span class="icon"><img src="@/app/assets/images/amber-exclamation.svg" alt="Warning" /></span>
      <span>We did not deduct a fee amount, in part or in whole, from this deposit. We will create a separate debit entry for it.</span>
    </div>

    <div class="fields" v-if="deposit">
      <div class="column">
        <FieldValue
          :field-value="getTransactionDateRange()"
          :field-name="$t('fieldNames.transactionsDate')"
        />
        <FieldValue
          :field-value="getSettledDate()"
          :field-name="$t('fieldNames.depositDate')"
        />
      </div>
      <div class="column">
        <FieldValue
          :field-value="getTotalAmount()"
          :field-name="$t('fieldNames.totalAmount')"
        />
        <FieldValue
          :field-value="getTotalFee()"
          :field-name="$t('fieldNames.deductions')"
        />
        <FieldValue
          :field-value="getDepositAmount()"
          :field-name="$t('fieldNames.depositAmount')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FieldValue from "@/components/common/FieldValue.vue";
import Formatter from "@/app/utils/common/functions/formatter";
import {DepositDto} from "@/api/paymentApi";

export default {
  name: "DepositOverview",
  components: {FieldValue},
  props: {
    deposit: {
      type: DepositDto,
      required: true,
    },
  },
  data: function () {
    return {}
  },
  methods: {
    formatTransactionsTimelineForASettlement: Formatter.formatTransactionsTimelineForASettlement,
    formatDateWithTimezone(date) {
      return Formatter.format({type: 'zonedDateWithTime', data: date})
    },
    format(type, data, format, currencyCode) {
      return Formatter.format({type, data, format, currencyCode})
    },

    // Get total fee from all settlements
    getTotalFee() {
      if (!this.deposit.settlements || this.deposit.settlements.length === 0) {
        return null;
      }

      const totalFee = this.deposit.settlements.reduce((total, settlement) => {
        return total + (settlement.total_fee || 0);
      }, 0);

      if (totalFee === 0) {
        return null;
      }

      return this.format('currency', totalFee, '', this.deposit.currency) + " " + this.deposit.currency;
    },

    // Get total amount from all settlements
    getTotalAmount() {
      if (!this.deposit.settlements || this.deposit.settlements.length === 0) {
        return this.format('currency', this.deposit.amount, '', this.deposit.currency) + " " + this.deposit.currency;
      }

      const totalAmount = this.deposit.settlements.reduce((total, settlement) => {
        return total + (settlement.total_amount || 0);
      }, 0);

      return this.format('currency', totalAmount, '', this.deposit.currency) + " " + this.deposit.currency;
    },

    // Get the actual deposit amount (could be from settlements or deposit.amount)
    getMerchantDepositAmount() {
      // Use the deposit amount directly as it represents the final deposit amount
      return this.deposit.type === 'DEBIT'
        ? this.deposit.amount * (-1)
        : this.deposit.amount;
    },

    getDepositAmount() {
      const amount = this.getMerchantDepositAmount();
      if (amount === undefined || amount === null) {
        return null;
      }
      return this.format('currency', amount, '', this.deposit.currency) + " " + this.deposit.currency;
    },

    // Get transaction date range from all settlements
    getTransactionDateRange() {
      if (!this.deposit.settlements || this.deposit.settlements.length === 0) {
        return null;
      }

      let firstDate = null;
      let lastDate = null;

      this.deposit.settlements.forEach(settlement => {
        if (settlement.transactions_timeline) {
          if (!firstDate || settlement.transactions_timeline.first_transaction_date < firstDate) {
            firstDate = settlement.transactions_timeline.first_transaction_date;
          }
          if (!lastDate || settlement.transactions_timeline.last_transaction_date > lastDate) {
            lastDate = settlement.transactions_timeline.last_transaction_date;
          }
        }
      });

      if (!firstDate || !lastDate) {
        return null;
      }

      const timeline = {
        first_transaction_date: firstDate,
        last_transaction_date: lastDate
      };

      return this.formatTransactionsTimelineForASettlement(timeline, 'zonedDate');
    },

    // Get the settled date (use the first settlement's settled_at date)
    getSettledDate() {
      if (!this.deposit.settlements || this.deposit.settlements.length === 0) {
        return null;
      }

      // Use the first settlement's settled_at date
      const settledAt = this.deposit.settlements[0].settled_at;
      if (!settledAt) {
        return null;
      }

      return this.formatDateWithTimezone(settledAt);
    },
  },
}
</script>

<style lang="scss">
.deposit-overview {
  & .title {
    text-align: left;
    font-size: 16px;
    font-weight: $weight-bold;
  }

  & .warning {
    margin-top: 12px;
    color: $warn_800;
    display: flex;
    font-size: 12px;
    text-align: left;

    & .icon {
      margin-right: 4px;
    }
  }

  & .fields {
    display: flex;

    & .column {
      text-align: left;
      flex: 1;
    }

    & .consumer-email {
      & a {
        text-decoration: none;
      }
    }

    & .source-transaction {
      color: $primary_600;
      cursor: pointer;
    }

    & .respond-by {
      & .field-value {
        color: #DE3618;
      }
    }
  }
}
</style>